/**
 * Conversation pane — message list + composer + dual streaming surfaces.
 *
 * Import flow (file attached):
 *   1. FAST PATH — POST /ingest (deterministic parser, no Claude credits used).
 *      Falls back to tally-chat-bll agent if the bank is unsupported or parse fails.
 *   2. AGENT PATH — streamTallyAgent() via axon-tally-chat-bll (Anthropic Agents SDK,
 *      real SSE streaming with tool-call visibility).
 *
 * Free-form text → secretary SSE stream.
 */

import { useEffect, useMemo, useRef, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { conversationsApi, deApi } from '@/lib/api';
import { streamChat, type StreamHandle } from '@/lib/sse';
import { startProgressPoll, type ProgressPollHandle } from '@/lib/progress';
import {
  runIngest,
  confirmIngest,
  makeProgressId,
  extractNgrokUrl,
  detectBankFromFilename,
  detectBankFromMessage,
  appendInferredRules,
  ApiError,
  createFrConversation,
  streamFrConversation,
  type IngestResult,
  type ConfirmResult,
  type FrDoneResult,
} from '@/lib/aiaccountant';
import { streamTallyAgent, type TallyBllStreamHandle } from '@/lib/tallyBll';
import { useSettings } from '@/lib/hooks';
import { Composer, type ComposerSubmit } from './Composer';
import { TallyStatusBar } from './TallyStatusBar';
import { Message } from './Message';
import { ToolCallStream } from './ToolCallStream';
import type { Message as ChatMessage, ProgressEvent, ToolStep } from '@/types/chat';
import { isBalanceDepleted, readBalance, type BalanceData } from '@/lib/billing';
import { persistTurn } from '@/lib/conversationCache';
import {
  rememberFile,
  getRememberedFile,
  getFileMeta,
  forgetFile,
  type FileMemoryMeta,
} from '@/lib/fileMemory';
import { Wallet, AlertTriangle, Paperclip, X as XIcon, Upload, RefreshCw } from 'lucide-react';

interface ConversationProps {
  conversationId: string;
  defaultSlash?: string;
  balance?: BalanceData | null;
  onOpenRecharge?:    () => void;
  onOpenSettings?:    () => void;
  onOpenMappingRules?:() => void;
}

interface StreamingState {
  buffer: string;
  progress: ProgressEvent[];
  toolSteps: ToolStep[];
  stage?: string;
  active: boolean;
  error?: string;
}

/** Tracks a parsed-but-not-yet-pushed import waiting for Tally to come online. */
interface PendingConfirm {
  sessionId:   string;
  tallyUrl:    string;
  count:       number;
  auto:        number;
  suspense:    number;
  confirming:  boolean;
  confirmError?: string;
}

const emptyStream: StreamingState = { buffer: '', progress: [], toolSteps: [], active: false };

export function Conversation({
  conversationId,
  defaultSlash,
  balance,
  onOpenRecharge,
  onOpenSettings,
  onOpenMappingRules,
}: ConversationProps) {
  const qc = useQueryClient();
  const scrollRef     = useRef<HTMLDivElement>(null);
  const streamRef     = useRef<StreamHandle | TallyBllStreamHandle | null>(null);
  const progRef       = useRef<ProgressPollHandle | null>(null);
  // Persists the BLL conversation id across follow-up turns so the agent keeps context.
  const bllConvIdRef  = useRef<string | null>(null);
  const [settings]  = useSettings();

  const { data: msgData, isLoading } = useQuery({
    queryKey: ['messages', conversationId],
    queryFn: () => conversationsApi.getMessages(conversationId),
    enabled: !!conversationId,
  });

  const { data: deData } = useQuery({
    queryKey: ['de-catalog'],
    queryFn: () => deApi.catalog(),
    staleTime: 5 * 60_000,
    retry: false,
  });

  const [streaming, setStreaming]         = useState<StreamingState>(emptyStream);
  const [optimisticUser, setOptimisticUser] = useState<ChatMessage | null>(null);
  const [pendingConfirm, setPendingConfirm] = useState<PendingConfirm | null>(null);
  const [rememberedMeta, setRememberedMeta] = useState<FileMemoryMeta | null>(
    () => getFileMeta(conversationId),
  );

  useEffect(() => { setRememberedMeta(getFileMeta(conversationId)); }, [conversationId]);

  // Auto-scroll
  useEffect(() => {
    const el = scrollRef.current;
    if (el) el.scrollTop = el.scrollHeight;
  }, [msgData, streaming.buffer, streaming.progress.length, optimisticUser]);

  // Cancel in-flight streams and clear BLL conversation on conversation switch / unmount
  useEffect(() => {
    bllConvIdRef.current = null;
    return () => {
      streamRef.current?.cancel();
      progRef.current?.stop();
    };
  }, [conversationId]);

  const messages = useMemo(() => {
    const list: ChatMessage[] = msgData?.messages ?? [];
    if (optimisticUser) return [...list, optimisticUser];
    return list;
  }, [msgData, optimisticUser]);

  const handleSubmit = async (s: ComposerSubmit) => {
    // File memory — rescue remembered file when user sends a follow-up without
    // attaching. After a page reload the in-memory cache is empty; getRememberedFile
    // falls back to IndexedDB and rehydrates so the user doesn't have to re-upload.
    let effectiveFiles = s.files;
    if (effectiveFiles.length === 0) {
      const remembered = await getRememberedFile(conversationId);
      if (remembered) effectiveFiles = [remembered];
    } else if (effectiveFiles.length > 0) {
      // New file attached — remember it AND clear any pending confirm from prior import
      rememberFile(conversationId, effectiveFiles[0]);
      setRememberedMeta(getFileMeta(conversationId));
      setPendingConfirm(null);    // new file = fresh import, old session irrelevant
      bllConvIdRef.current = null; // new upload always starts a fresh BLL conversation
    }

    // Optimistic user bubble
    setOptimisticUser({
      id: `tmp-${Date.now()}`,
      conversation_id: conversationId,
      role: 'user',
      content: s.message,
      created_at: new Date().toISOString(),
      attachments: effectiveFiles.length
        ? effectiveFiles.map((f, i) => ({
            id: `tmp-${i}`,
            filename: f.name,
            mime_type: f.type || 'application/octet-stream',
            size_bytes: f.size,
          }))
        : undefined,
    });
    setStreaming({ ...emptyStream, active: true });

    const submission: ComposerSubmit = { ...s, files: effectiveFiles };

    const slashToUse    = submission.slash ?? (submission.files.length > 0 ? defaultSlash : undefined);
    const isTallyImport = slashToUse === '/tally-import';
    const isFinancialReport = slashToUse === '/financial-report';
    const hasFiles      = submission.files.length > 0;

    if (isTallyImport || hasFiles) {
      await runImportFlow(submission);
    } else if (isFinancialReport) {
      await runReportFlow(submission);
    } else if (bllConvIdRef.current) {
      // Active tally-bll session — route follow-up to the same BLL conversation
      // so the agent retains full context (session_id, pushed_offset, etc.)
      _runAgentPath(submission, undefined, settings.tally_url || '', '', '');
    } else {
      runChatFlow(submission);
    }
  };

  // ── Fast-path: try /ingest first, fall back to /agent ─────────────────────

  async function runImportFlow(s: ComposerSubmit) {
    const progressId = makeProgressId();
    const file       = s.files[0];

    // Extract ngrok URL typed in message (e.g. "import at https://abc.ngrok-free.app")
    const ngrokFromMsg = extractNgrokUrl(s.message);
    const effectiveTallyUrl = ngrokFromMsg || settings.tally_url || '';

    // Detect bank from filename and/or message text
    const bankId =
      (file ? detectBankFromFilename(file.name) : '') ||
      detectBankFromMessage(s.message) ||
      '';

    // Stop any poll from a previous attempt before starting a new one.
    // Without this, retries accumulate zombie polls that run forever (the
    // server returns HTTP 200 with empty events — never an error — so
    // maxConsecutiveErrors never triggers).
    progRef.current?.stop();
    progRef.current = startProgressPoll(progressId, {
      onEvent: (ev) =>
        setStreaming((cur) => ({ ...cur, progress: [...cur.progress, ev] })),
      onError: (err) =>
        setStreaming((cur) => ({ ...cur, active: false, error: err.message })),
    });

    try {
      // ── FAST PATH: /ingest (deterministic, no Claude) ─────────────────────
      if (file) {
        // Show upload stage label during the (potentially long) multipart upload.
        const sizeMb = (file.size / (1024 * 1024)).toFixed(1);
        setStreaming((cur) => ({ ...cur, stage: `Uploading ${file.name} (${sizeMb} MB)…` }));

        let ingestResult: IngestResult;
        try {
          ingestResult = await runIngest({
            file,
            tallyUrl:   effectiveTallyUrl,
            companyId:  settings.company_id,
            bankLedger: settings.bank_ledger || undefined,
            bankId:     bankId || undefined,
            progressId,
          });
        } catch (ingestErr) {
          // Known unsupported types / parser failures → fall through to /agent
          const code = ingestErr instanceof ApiError ? ingestErr.code : '';
          const isParserMiss = (
            code === 'UNSUPPORTED_TYPE' ||
            code === 'UNSUPPORTED_BANK' ||
            code === 'PARSE_ERROR' ||
            // Catch any message that suggests the parser couldn't handle it
            (ingestErr as Error).message?.toLowerCase().includes('unsupported')
          );
          if (isParserMiss) {
            // Fall through to /agent below
            return await _runAgentPath(s, file, effectiveTallyUrl, bankId, progressId);
          }
          // Network error / auth error / timeout → re-throw
          throw ingestErr;
        }

        // Upload + parse succeeded — clear the uploading label
        setStreaming((cur) => ({ ...cur, stage: undefined }));

        setTimeout(() => progRef.current?.stop(), 3000);

        // Post-import: save auto-inferred narration patterns to DB
        if (ingestResult.inferred_rules_count > 0 && settings.company_id) {
          appendInferredRules(settings.company_id, ingestResult.inferred_rules).catch(() => {});
        }

        // If Tally was offline at parse time, keep the session so the user can
        // click "Push to Tally" later. Otherwise auto-confirm right now —
        // /ingest only parses + stores XML; nothing is pushed until /ingest-confirm
        // is called with the session_id.
        let confirmOutcome: ConfirmResult | undefined;
        let confirmFailed = false;
        let confirmErrMsg = '';

        if (ingestResult.tally_offline) {
          setPendingConfirm({
            sessionId:  ingestResult.session_id,
            tallyUrl:   effectiveTallyUrl,
            count:      ingestResult.count,
            auto:       ingestResult.auto,
            suspense:   ingestResult.suspense,
            confirming: false,
          });
        } else if (effectiveTallyUrl && ingestResult.session_id) {
          // Tally is online → push automatically. This is the missing step that
          // used to leave the import in "pushing to Tally now" purgatory.
          try {
            confirmOutcome = await confirmIngest({
              sessionId: ingestResult.session_id,
              tallyUrl:  effectiveTallyUrl,
            });
            if (!confirmOutcome.success) {
              confirmFailed = true;
              confirmErrMsg = confirmOutcome.user_message
                ?? confirmOutcome.message
                ?? 'Tally rejected the import';
              if (confirmOutcome.blocked) {
                // Gate blocked — "Retry push" won't help (bad data); dismiss the banner.
                setPendingConfirm(null);
              } else {
                setPendingConfirm({
                  sessionId:  ingestResult.session_id,
                  tallyUrl:   effectiveTallyUrl,
                  count:      ingestResult.count,
                  auto:       ingestResult.auto,
                  suspense:   ingestResult.suspense,
                  confirming: false,
                  confirmError: confirmErrMsg,
                });
              }
            }
          } catch (confirmErr) {
            confirmFailed = true;
            confirmErrMsg = (confirmErr as Error).message ?? 'Push to Tally failed';
            // Keep the session live so the user can retry via the banner
            setPendingConfirm({
              sessionId:  ingestResult.session_id,
              tallyUrl:   effectiveTallyUrl,
              count:      ingestResult.count,
              auto:       ingestResult.auto,
              suspense:   ingestResult.suspense,
              confirming: false,
              confirmError: confirmErrMsg,
            });
          }
        }

        // Format a rich markdown response — confirm outcome is woven in
        const response = formatIngestResponse(ingestResult, {
          confirmOutcome,
          confirmFailed,
          confirmErrMsg,
          tallyUrlPresent: !!effectiveTallyUrl,
          confirmBlocked: confirmOutcome?.blocked,
        });
        // Rename the conversation to "{Company} — {Bank}" so the sidebar is
        // scannable. Falls back gracefully when company is unset.
        const newTitle = buildImportTitle({
          companyId: settings.company_id,
          bankId:    ingestResult.detected_bank || bankId,
          fileName:  file.name,
        });
        _commitToCache(s, file, response, progressId, undefined, newTitle);
        return;
      }

      // ── No file → always /agent (follow-up, free-form, etc.) ─────────────
      await _runAgentPath(s, undefined, effectiveTallyUrl, bankId, progressId);
    } catch (err) {
      progRef.current?.stop();
      const msg = err instanceof ApiError
        ? `${err.code}: ${err.message}`
        : (err as Error).message;
      setStreaming((cur) => ({ ...cur, active: false, error: msg }));
    }
  }

  /** Agent path — live SSE streaming via axon-tally-chat-bll (Anthropic Agents SDK). */
  function _runAgentPath(
    s: ComposerSubmit,
    file: File | undefined,
    tallyUrl: string,
    bankId: string,
    _progressId: string,
  ) {
    // tally-bll has its own event-based progress; the fast-path poll is not needed.
    progRef.current?.stop();

    const message = s.message || (file ? `Import ${file?.name}` : 'Import');

    streamRef.current = streamTallyAgent(
      {
        message,
        file,
        tallyUrl,
        companyId:   settings.company_id,
        bankLedger:  settings.bank_ledger || undefined,
        bankId:      bankId || undefined,
        existingConvId: bllConvIdRef.current ?? undefined,
      },
      {
        onConversationCreated: (convId) => {
          bllConvIdRef.current = convId;
        },

        onTextDelta: (text) => {
          setStreaming((cur) => ({ ...cur, buffer: cur.buffer + text }));
        },

        onThinkingDelta: (text) => {
          setStreaming((cur) => ({ ...cur, stage: text.slice(0, 80) }));
        },

        onToolUse: (tool, id) => {
          // Finalize or abort ends the BLL session — next message starts fresh
          if (tool === 'tally_finalize_session' || tool === 'tally_abort_import') {
            bllConvIdRef.current = null;
          }
          setStreaming((cur) => {
            const closedPrev = cur.toolSteps.map((step) =>
              step.status === 'running' ? { ...step, status: 'done' as const } : step,
            );
            return {
              ...cur,
              toolSteps: [...closedPrev, {
                tool,
                step: closedPrev.length + 1,
                status: 'running' as const,
                label: id,
              }],
            };
          });
        },

        onToolResult: (id, preview, isError) => {
          setStreaming((cur) => ({
            ...cur,
            toolSteps: cur.toolSteps.map((step) =>
              step.label === id
                ? { ...step, status: isError ? 'error' as const : 'done' as const, detail: preview.slice(0, 120) }
                : step,
            ),
          }));
        },

        onDone: ({ finalText, isError, costUsd }) => {
          progRef.current?.stop();
          if (isError) {
            setStreaming((cur) => ({
              ...cur,
              active: false,
              error: finalText || 'Agent returned an error. Check Tally connection.',
            }));
            setOptimisticUser(null);
            return;
          }
          const usage = costUsd !== undefined
            ? { cost_usd_raw: costUsd } as Record<string, unknown>
            : undefined;
          _commitToCache(s, file, finalText, '', usage ? { usage } : undefined);
        },

        onError: (msg) => {
          progRef.current?.stop();
          setStreaming((cur) => ({ ...cur, active: false, error: msg }));
          setOptimisticUser(null);
        },
      },
    );
  }

  /** Persist exchange to React Query cache + localStorage. Clears streaming state. */
  function _commitToCache(
    s: ComposerSubmit,
    file: File | undefined,
    responseText: string,
    _progressId: string,
    agentResult?: { tool_calls?: unknown[]; usage?: Record<string, unknown> },
    conversationTitle?: string,
  ) {
    const now = new Date().toISOString();
    const userMsg: ChatMessage = {
      id: `user-${Date.now()}`,
      conversation_id: conversationId,
      role: 'user',
      content: s.message || (file ? `Import ${file.name}` : ''),
      created_at: now,
      attachments: file ? [{
        id: `att-${Date.now()}`,
        filename: file.name,
        mime_type: file.type || 'application/octet-stream',
        size_bytes: file.size,
      }] : undefined,
    };
    const assistantMsg: ChatMessage = {
      id: `assistant-${Date.now() + 1}`,
      conversation_id: conversationId,
      role: 'assistant',
      content: responseText,
      created_at: new Date(Date.now() + 1).toISOString(),
      tool_calls: agentResult?.tool_calls?.map((tc: unknown, i: number) => {
        const t = tc as {
          tool: string;
          input_summary?: Record<string, string>;
          result?: string;
          review_queue?: unknown;
          financial_report?: unknown;
          inferred_rules?: unknown;
          inferred_rules_count?: number;
          history_vouchers_scanned?: number;
          company_id?: string;
        };
        return {
          // Spread all rich payload fields first so they survive the round-trip
          ...(t.review_queue            !== undefined && { review_queue:             t.review_queue }),
          ...(t.financial_report        !== undefined && { financial_report:         t.financial_report }),
          ...(t.inferred_rules          !== undefined && { inferred_rules:           t.inferred_rules }),
          ...(t.inferred_rules_count    !== undefined && { inferred_rules_count:     t.inferred_rules_count }),
          ...(t.history_vouchers_scanned !== undefined && { history_vouchers_scanned: t.history_vouchers_scanned }),
          ...(t.company_id              !== undefined && { company_id:               t.company_id }),
          id: `tc-${i}`,
          tool: t.tool,
          input_preview: t.input_summary
            ? Object.entries(t.input_summary).map(([k, v]) => `${k}=${v}`).join(' · ')
            : undefined,
          result_summary: t.result?.slice(0, 200),
          status: 'success' as const,
        };
      }),
      usage: agentResult?.usage as ChatMessage['usage'],
    };

    persistTurn(conversationId, userMsg, assistantMsg, conversationTitle);
    qc.setQueryData<{ messages: ChatMessage[] }>(
      ['messages', conversationId],
      (old) => {
        const oldMsgs = old?.messages ?? [];
        const filtered = oldMsgs.filter((m) => m.id !== userMsg.id && m.id !== assistantMsg.id);
        return { messages: [...filtered, userMsg, assistantMsg] };
      },
    );
    setStreaming((cur) => ({ ...cur, active: false, buffer: responseText }));
    setOptimisticUser(null);

    // Refresh balance from agent usage if available
    const balAfter = (agentResult?.usage as { balance_after?: number } | undefined)?.balance_after;
    if (typeof balAfter === 'number') {
      qc.setQueryData(['billing-balance'], (old: Record<string, unknown> | undefined) => ({
        ...(old ?? {}), credits_balance: balAfter,
      }));
    }
    window.dispatchEvent(new CustomEvent('axondesktop.billing.refreshed'));
    qc.invalidateQueries({ queryKey: ['billing-balance'] });
    qc.invalidateQueries({ queryKey: ['conversations'] });
  }

  /** Push a pending import to Tally (called when user clicks "Push to Tally"). */
  const handleConfirmImport = async () => {
    if (!pendingConfirm) return;
    setPendingConfirm((p) => p ? { ...p, confirming: true, confirmError: undefined } : p);
    try {
      const outcome = await confirmIngest({
        sessionId: pendingConfirm.sessionId,
        tallyUrl:  pendingConfirm.tallyUrl,
      });
      if (outcome.blocked) {
        // Gate blocked — session data is bad; dismiss the banner.
        setPendingConfirm(null);
        const blockedMsg: ChatMessage = {
          id: `assistant-blocked-${Date.now()}`,
          conversation_id: conversationId,
          role: 'assistant',
          content: `❌ **Import blocked** — ${outcome.user_message ?? 'Data did not pass validation.'}` +
                   '\n\n**Re-upload the file** to start a fresh import.',
          created_at: new Date().toISOString(),
        };
        qc.setQueryData<{ messages: ChatMessage[] }>(
          ['messages', conversationId],
          (old) => ({ messages: [...(old?.messages ?? []), blockedMsg] }),
        );
        return;
      }
      setPendingConfirm(null);
      // Add success message to chat
      const successMsg: ChatMessage = {
        id: `assistant-confirm-${Date.now()}`,
        conversation_id: conversationId,
        role: 'assistant',
        content: `✅ **${pendingConfirm.count} transactions pushed to TallyPrime** — import complete.`,
        created_at: new Date().toISOString(),
      };
      qc.setQueryData<{ messages: ChatMessage[] }>(
        ['messages', conversationId],
        (old) => ({ messages: [...(old?.messages ?? []), successMsg] }),
      );
    } catch (e) {
      setPendingConfirm((p) =>
        p ? { ...p, confirming: false, confirmError: (e as Error).message } : p,
      );
    }
  };

  /** Financial report agent — streaming via axon-tally-chat-bll (Anthropic Agents SDK). */
  async function runReportFlow(s: ComposerSubmit) {
    // Extract FY from user message e.g. "FY 2023-24" or "2023-24"
    const fyMatch = s.message.match(/(20\d\d)[–\-](20\d\d|\d\d)/);
    const fyFrom  = fyMatch ? `${fyMatch[1]}0401` : '';
    const fyTo    = fyMatch ? `${String(Number(fyMatch[1]) + 1)}0331` : '';

    const periodLabel = fyFrom
      ? `FY ${fyFrom.slice(0, 4)}–${fyTo.slice(0, 4)}`
      : 'Current FY';

    let convId: string;
    try {
      const conv = await createFrConversation({
        tenantId:  settings.company_id || 'default',
        tallyUrl:  settings.tally_url  || '',
        companyId: settings.company_id || '',
        fyFrom,
        fyTo,
      });
      convId = conv.id;
    } catch (err) {
      const msg = err instanceof ApiError ? `${err.code}: ${err.message}` : (err as Error).message;
      setStreaming((cur) => ({ ...cur, active: false, error: msg }));
      return;
    }

    streamRef.current = streamFrConversation(
      convId,
      s.message,
      settings.company_id || 'default',
      {
        onTextDelta: (text) =>
          setStreaming((cur) => ({ ...cur, buffer: cur.buffer + text })),

        onToolUse: (tool, step) =>
          setStreaming((cur) => {
            const closed = cur.toolSteps.map((ts) =>
              ts.status === 'running' ? { ...ts, status: 'done' as const } : ts,
            );
            return {
              ...cur,
              toolSteps: [...closed, { tool, step, status: 'running', label: tool }],
            };
          }),

        onToolDone: (tool, step) =>
          setStreaming((cur) => ({
            ...cur,
            toolSteps: cur.toolSteps.map((ts) =>
              ts.step === step ? { ...ts, status: 'done' as const } : ts,
            ),
          })),

        onDone: (result: FrDoneResult) => {
          // Find the generate_financial_report tool call to extract download_url
          const frTc = result.tool_calls?.find(
            (tc) => (tc as { tool: string }).tool === 'generate_financial_report',
          );
          let downloadUrl: string | undefined;
          if (frTc?.result) {
            try {
              const parsed = JSON.parse(frTc.result as string) as {
                success?: boolean;
                data?: { download_url?: string };
              };
              downloadUrl = parsed?.data?.download_url;
            } catch { /* result wasn't JSON */ }
          }

          const financialReportCard = {
            kind:         'OTHER' as const,
            title:        'Financial Report',
            period:       periodLabel,
            company:      settings.company_id || '',
            sections:     [] as Array<{
              heading: string;
              rows: Array<{ label: string; amount?: number; bold?: boolean; indent?: number }>;
              subtotal?: number;
            }>,
            download_url: downloadUrl,
            notes:        result.response ? [result.response.slice(0, 300)] : undefined,
          };

          const syntheticAgentResult = {
            tool_calls: [{
              tool:             'generate_financial_report',
              financial_report: financialReportCard,
              result:           result.response,
            }],
            usage: result.usage,
          };

          _commitToCache(s, undefined, result.response, '', syntheticAgentResult);
        },

        onError: (msg) =>
          setStreaming((cur) => ({ ...cur, active: false, error: msg })),
      },
    );
  }

  /** Free-form chat via secretary SSE. */
  function runChatFlow(s: ComposerSubmit) {
    streamRef.current = streamChat(conversationId, s.message, {
      onEvent: (ev) => {
        if (ev.event === 'message') {
          setStreaming((cur) => ({ ...cur, buffer: cur.buffer + ev.data.content }));
        } else if (ev.event === 'thinking') {
          setStreaming((cur) => ({ ...cur, stage: ev.data.stage }));
        } else if (ev.event === 'progress') {
          const d = ev.data;
          setStreaming((cur) => {
            const closedPrev = cur.toolSteps.map((s) =>
              s.status === 'running' ? { ...s, status: 'done' as const } : s,
            );
            return {
              ...cur,
              toolSteps: [...closedPrev, {
                tool: d.tool || 'tool',
                step: d.step ?? closedPrev.length + 1,
                status: 'running',
                label: d.message,
                params: d.params,
                paramsSummary: d.params_summary,
              }],
            };
          });
        } else if (ev.event === 'tool_completed') {
          const d = ev.data;
          setStreaming((cur) => ({
            ...cur,
            toolSteps: cur.toolSteps.map((step) => {
              const matches =
                (d.step != null && step.step === d.step) ||
                (d.step == null && step.tool === d.tool && step.status === 'running');
              if (!matches) return step;
              return { ...step, status: d.success === false ? 'error' : 'done', error: d.error ?? null };
            }),
          }));
        } else if (ev.event === 'done') {
          setStreaming((cur) => ({
            ...cur, active: false,
            toolSteps: cur.toolSteps.map((s) =>
              s.status === 'running' ? { ...s, status: 'done' as const } : s,
            ),
          }));
          qc.invalidateQueries({ queryKey: ['messages', conversationId] });
          qc.invalidateQueries({ queryKey: ['conversations'] });
          window.dispatchEvent(new CustomEvent('axondesktop.billing.refreshed'));
          qc.invalidateQueries({ queryKey: ['billing-balance'] });
          setOptimisticUser(null);
        } else if (ev.event === 'error') {
          setStreaming((cur) => ({ ...cur, active: false, error: ev.data.message }));
        }
      },
      onClose: () => setStreaming((cur) => ({ ...cur, active: false })),
      onError: (err) => setStreaming((cur) => ({ ...cur, active: false, error: err.message })),
    });
  }

  const stop = () => {
    streamRef.current?.cancel();
    progRef.current?.stop();
    setStreaming((cur) => ({ ...cur, active: false }));
  };

  return (
    <div className="flex-1 flex flex-col h-full overflow-hidden">
      <div ref={scrollRef} className="flex-1 overflow-y-auto px-8 py-6">
        {isLoading && <div className="text-sm text-muted-foreground">Loading messages…</div>}
        {messages.length === 0 && !isLoading && <EmptyState />}

        {messages.map((m) => (
          <Message key={m.id} message={m} />
        ))}

        {streaming.active && (
          <>
            {(streaming.toolSteps.length > 0 ||
              (streaming.buffer.length === 0 && streaming.progress.length === 0)) && (
              <ToolCallStream steps={streaming.toolSteps} thinkingStage={streaming.stage} />
            )}
            {(streaming.buffer.length > 0 || streaming.progress.length > 0) && (
              <Message
                message={{
                  id: 'streaming',
                  conversation_id: conversationId,
                  role: 'assistant',
                  content: streaming.buffer,
                  created_at: new Date().toISOString(),
                }}
                liveProgress={streaming.progress}
                streaming
              />
            )}
          </>
        )}

        {streaming.error && (
          /BALANCE_DEPLETED|insufficient|recharge/i.test(streaming.error) ? (
            <div className="text-sm bg-amber-50 border border-amber-300 text-amber-900 rounded-md p-3 mt-2 flex flex-col items-start gap-2">
              <div className="font-medium">Balance depleted</div>
              <div>{streaming.error}</div>
              {onOpenRecharge && (
                <button
                  onClick={onOpenRecharge}
                  className="px-3 py-1.5 rounded bg-primary text-primary-foreground text-xs font-semibold hover:bg-primary/90 flex items-center gap-1.5"
                >
                  <Wallet className="w-3.5 h-3.5" /> Add credits
                </button>
              )}
            </div>
          ) : (
            <div className="text-sm bg-destructive/10 border border-destructive text-destructive rounded-md p-3 mt-2">
              <div className="font-medium mb-1">Error</div>
              {streaming.error}
            </div>
          )
        )}
      </div>

      {isBalanceDepleted(balance) ? (
        <BalanceDepleted onRecharge={onOpenRecharge} />
      ) : (
        <>
          {readBalance(balance) > 0 && readBalance(balance) <= 49 && (
            <LowBalanceBanner balance={readBalance(balance)} onRecharge={onOpenRecharge} />
          )}
          {pendingConfirm && (
            <PendingConfirmBanner
              pending={pendingConfirm}
              onConfirm={handleConfirmImport}
              onDismiss={() => setPendingConfirm(null)}
            />
          )}
          {rememberedMeta && !pendingConfirm && (
            <RememberedFileHint
              meta={rememberedMeta}
              onForget={() => { forgetFile(conversationId); setRememberedMeta(null); }}
            />
          )}
          {onOpenSettings && onOpenRecharge && onOpenMappingRules && (
            <TallyStatusBar
              onOpenSettings={onOpenSettings}
              onOpenRecharge={onOpenRecharge}
              onOpenMappingRules={onOpenMappingRules}
            />
          )}
          <Composer
            onSubmit={handleSubmit}
            des={deData?.bindings ?? []}
            busy={streaming.active}
            defaultSlash={defaultSlash}
            onStop={streaming.active ? stop : undefined}
          />
        </>
      )}
    </div>
  );
}

// ── Helpers ───────────────────────────────────────────────────────────────────

/**
 * Friendly bank display name from the internal bank_id used by the parser
 * registry. Falls back to a title-cased / upper-cased rendering of the raw id.
 */
function bankDisplayName(bankId: string | undefined | null): string {
  if (!bankId) return '';
  const id = bankId.toLowerCase().trim();
  const map: Record<string, string> = {
    hdfc:           'HDFC Bank',
    sbi:            'SBI',
    sbi_passbook:   'SBI',
    canara:         'Canara Bank',
    cub:            'City Union Bank',
    icici:          'ICICI Bank',
    axis:           'Axis Bank',
    kotak:          'Kotak Mahindra',
    idfc_first:     'IDFC FIRST',
    idbi:           'IDBI Bank',
    central_bank:   'Central Bank of India',
    bank_of_baroda: 'Bank of Baroda',
    pnb:            'PNB',
    sib:            'South Indian Bank',
    dbs:            'DBS Bank',
    au:             'AU Small Finance',
    yes:            'YES Bank',
    indusind:       'IndusInd Bank',
  };
  if (map[id]) return map[id];
  // Generic conversion: snake_case → Title Case
  return id
    .split(/[_\s]+/)
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
    .join(' ');
}

/**
 * Build a friendly conversation title like "poonam Nahar — Bank of Baroda".
 * Falls back gracefully when company or bank is missing.
 */
function buildImportTitle(opts: {
  companyId?: string;
  bankId?: string | null;
  fileName?: string;
}): string {
  const company = (opts.companyId || '').trim();
  const bank    = bankDisplayName(opts.bankId);
  if (company && bank) return `${company} — ${bank}`;
  if (company)         return `${company} — Bank statement`;
  if (bank)            return `${bank} statement`;
  // Last-resort: strip extension from filename
  if (opts.fileName) {
    return opts.fileName.replace(/\.[^.]+$/, '').slice(0, 60) || 'Bank statement';
  }
  return 'Bank statement';
}

/** Format a rich markdown summary from /ingest stats + auto-confirm outcome. */
function formatIngestResponse(
  r: IngestResult,
  opts: {
    confirmOutcome?: ConfirmResult;
    confirmFailed?: boolean;
    confirmErrMsg?: string;
    tallyUrlPresent?: boolean;
    confirmBlocked?: boolean;
  } = {},
): string {
  const bankLabel = bankDisplayName(r.detected_bank) || 'Unknown bank';
  const tierLabel = r.tier_used === 1
    ? 'rule-based parser'
    : r.tier_used === 2
    ? 'generic text parser'
    : 'OCR (image scan)';

  const lines: string[] = [
    `### ✅ Bank statement parsed — ${bankLabel}`,
    '',
    `| | |`,
    `|---|---|`,
    `| Parser | Tier ${r.tier_used} — ${tierLabel} |`,
    `| Total transactions | **${r.count}** |`,
    `| Auto-mapped | **${r.auto}** ✓ |`,
    ...(r.review > 0 ? [`| Needs review | **${r.review}** ⚠️ |`] : []),
    ...(r.suspense > 0 ? [`| Suspense | **${r.suspense}** |`] : []),
    ...(r.drawings > 0 ? [`| Drawings (UPI) | **${r.drawings}** |`] : []),
    `| Debit total | ₹${r.debit_total.toLocaleString('en-IN')} |`,
    `| Credit total | ₹${r.credit_total.toLocaleString('en-IN')} |`,
    `| Time | ${r.elapsed_sec}s |`,
  ];

  // Tally push outcome — show the REAL result, not the misleading "pushing now"
  if (r.tally_offline) {
    lines.push(
      '',
      '⚠️ **TallyPrime was offline at parse time** — XML saved to session. ' +
      'Click **Push to Tally** below when Tally is back.',
    );
  } else if (!opts.tallyUrlPresent) {
    lines.push(
      '',
      'ℹ️ **No Tally URL configured** — XML generated but not pushed. ' +
      'Set a Tally URL in the status bar to enable auto-push.',
    );
  } else if (opts.confirmFailed) {
    if (opts.confirmBlocked) {
      lines.push(
        '',
        `❌ **Import blocked** — ${opts.confirmErrMsg ?? 'The parsed data did not pass validation.'}`,
        '',
        '**Re-upload the file** to start a fresh import.',
      );
    } else {
      lines.push(
        '',
        `❌ **Push to TallyPrime failed** — ${opts.confirmErrMsg ?? 'unknown error'}. ` +
        'Click **Retry push** in the banner above to try again.',
      );
    }
  } else if (opts.confirmOutcome?.success) {
    const created = opts.confirmOutcome.created ?? 0;
    const errors  = opts.confirmOutcome.errors  ?? 0;
    if (errors > 0) {
      lines.push(
        '',
        `⚠️ **Imported to TallyPrime with errors** — ${created} vouchers created, ${errors} rejected. ` +
        (opts.confirmOutcome.message ? `Details: ${opts.confirmOutcome.message}` : ''),
      );
    } else {
      lines.push(
        '',
        `✅ **Imported to TallyPrime** — **${created || r.count} vouchers** pushed successfully.`,
      );
    }
  } else {
    // /ingest succeeded with online Tally but no confirm outcome was attached
    // (shouldn't normally happen — defensive fallback).
    lines.push(
      '',
      'ℹ️ **Parsing complete** — push to Tally is pending. Session id: `' + r.session_id + '`',
    );
  }

  if (r.inferred_rules_count > 0) {
    lines.push('', `💡 *${r.inferred_rules_count} new narration patterns learned and saved to mapping rules.*`);
  }

  return lines.join('\n');
}

// ── Sub-components ────────────────────────────────────────────────────────────

function PendingConfirmBanner({
  pending,
  onConfirm,
  onDismiss,
}: {
  pending: PendingConfirm;
  onConfirm: () => void;
  onDismiss: () => void;
}) {
  return (
    <div className="border-t border-amber-400/30 bg-amber-500/8 px-4 py-2 flex items-center justify-between gap-3 text-xs">
      <span className="text-amber-800 flex items-center gap-1.5">
        <Upload className="w-3.5 h-3.5 shrink-0" />
        <span>
          <strong>{pending.count}</strong> transactions parsed ({pending.auto} mapped
          {pending.suspense > 0 ? `, ${pending.suspense} suspense` : ''}) — Tally was offline.
        </span>
      </span>
      <div className="flex items-center gap-2 shrink-0">
        {pending.confirmError && (
          <span className="text-destructive">{pending.confirmError}</span>
        )}
        <button
          onClick={onDismiss}
          className="text-muted-foreground hover:text-foreground px-1 py-0.5 rounded hover:bg-muted"
        >
          Dismiss
        </button>
        <button
          onClick={onConfirm}
          disabled={pending.confirming}
          className="flex items-center gap-1.5 px-2.5 py-1 rounded bg-primary text-primary-foreground hover:bg-primary/90 disabled:opacity-60 font-medium"
        >
          {pending.confirming ? (
            <><RefreshCw className="w-3 h-3 animate-spin" /> Pushing…</>
          ) : (
            <>Push to Tally</>
          )}
        </button>
      </div>
    </div>
  );
}

function RememberedFileHint({ meta, onForget }: { meta: FileMemoryMeta; onForget: () => void }) {
  const sizeKb = (meta.size_bytes / 1024).toFixed(0);
  return (
    <div className="border-t border-border bg-muted/40 px-4 py-1.5 flex items-center justify-between text-xs">
      <span className="flex items-center gap-1.5 text-muted-foreground">
        <Paperclip className="w-3 h-3" />
        Using last uploaded file: <span className="font-medium text-foreground">{meta.filename}</span>
        <span className="text-muted-foreground/60">({sizeKb} KB)</span>
      </span>
      <button
        onClick={onForget}
        className="text-muted-foreground hover:text-foreground p-0.5 rounded hover:bg-muted"
        title="Forget — I'll upload a fresh file"
      >
        <XIcon className="w-3 h-3" />
      </button>
    </div>
  );
}

function BalanceDepleted({ onRecharge }: { onRecharge?: () => void }) {
  return (
    <div className="border-t border-border bg-destructive/5 p-4">
      <div className="max-w-lg mx-auto flex flex-col items-center gap-2 text-center">
        <AlertTriangle className="w-6 h-6 text-destructive" />
        <p className="text-sm">
          Your balance is <span className="text-destructive font-semibold">₹0</span>. Recharge to continue.
        </p>
        <button
          onClick={onRecharge}
          className="flex items-center gap-2 px-4 py-2 bg-primary text-primary-foreground text-sm font-semibold rounded-lg hover:bg-primary/90"
        >
          <Wallet className="w-4 h-4" /> Recharge Now
        </button>
      </div>
    </div>
  );
}

function LowBalanceBanner({ balance, onRecharge }: { balance: number; onRecharge?: () => void }) {
  return (
    <div className="border-t border-amber-500/20 bg-amber-500/5 px-4 py-1.5 flex items-center justify-between text-xs">
      <span className="text-amber-700 flex items-center gap-1.5">
        <AlertTriangle className="w-3.5 h-3.5" />
        Low balance: ₹{balance.toFixed(2)} — top up to keep using Laabha.
      </span>
      <button onClick={onRecharge} className="px-2 py-0.5 rounded text-amber-700 hover:bg-amber-500/10 font-medium">
        Add credits
      </button>
    </div>
  );
}

function EmptyState() {
  return (
    <div className="h-full flex flex-col items-center justify-center text-center text-muted-foreground gap-2">
      <img src="/desktop/laabha.png" alt="Laabha" className="h-10 w-auto mb-2" draggable={false} />
      <div className="text-lg font-medium text-foreground">Laabha · AIAccountant</div>
      <div className="text-sm max-w-md">
        Drag-drop a bank statement PDF or Excel to import to Tally, ask for a financial report, or just chat.
      </div>
      <div className="grid grid-cols-1 gap-1.5 mt-4 text-xs">
        <SamplePrompt>📄 Import this HDFC statement to Tally (drop a PDF)</SamplePrompt>
        <SamplePrompt>💰 Generate P&L for FY 2024-25</SamplePrompt>
        <SamplePrompt>📊 Show outstanding receivables</SamplePrompt>
      </div>
      <div className="text-xs text-muted-foreground mt-6 max-w-md">
        Tip: configure your Tally ngrok URL once in Settings (gear icon) — every import re-uses it.
        You can also type a fresh URL inline: "import at https://abc.ngrok-free.app"
      </div>
    </div>
  );
}

function SamplePrompt({ children }: { children: React.ReactNode }) {
  return (
    <div className="px-3 py-2 rounded-md border border-border bg-card hover:bg-muted/50 cursor-default">
      {children}
    </div>
  );
}
